import React, { useEffect } from 'react';
import { Person } from 'talent_matching/types';
import Collapsible from '@grnhse/seedling/lib/birch/components/collapsible';
import Container from '@grnhse/seedling/lib/birch/components/container';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { withLocalePrefix } from 'shared/utils/translation';
import FraudReport from 'fraud/components/fraud_report';
import styled from 'styled-components';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { useUserContext } from 'talent_matching/contexts/user_context';
import {
  useFraudReport,
  useFraudReportActions,
} from 'talent_matching/hooks/use_fraud_report';
import { usePerson } from 'talent_matching/hooks/use_people';
import { isItemUnread } from 'candidate_profile_redesign/shared/utils';
import CollapsibleTitle from 'candidate_profile_redesign/rightbar/candidate_details/collapsible_title';
import { FraudLoading } from 'candidate_profile_redesign/rightbar/security_screening/loading';

const t = withLocalePrefix('talent_matching');

const BorderContainer = styled(Container)`
  padding: ${Tokens.spacing['2']} 0;
`;

type Props = {
  application: Person;
  openFraudModal: () => void;
  onResolveFraudReport?: () => void;
  openRejectModal?: () => void;
  canReject?: boolean;
};

const FraudReportCollapsible = ({
  application,
  openFraudModal,
  onResolveFraudReport,
  openRejectModal,
  canReject,
}: Props) => {
  const [showReport, setShowReport] = React.useState<boolean>(
    !!application?.fraudReportHighRisk
  );
  const { canRunFraudReports, canUseSecurityConcerns } = useUserContext();
  const { person } = usePerson(application.id);
  const { data: fraudReport, isPending } = useFraudReport(application);
  const {
    request: requestFraudReport,
    retryFailed: retryFailedFraudReport,
    restore: restoreFraudReport,
    updateLastSeen: updateLastSeenFraudReport,
  } = useFraudReportActions(application);

  const isFraudReportUnread =
    !isPending && isItemUnread(person.lastSeenFraudReport, fraudReport);

  useEffect(() => {
    if (isFraudReportUnread && showReport) {
      updateLastSeenFraudReport();
    }
  }, [isFraudReportUnread, showReport, updateLastSeenFraudReport]);

  const subtitle = fraudReport?.generated_at
    ? t('panel.fraud_report.run_on_date', { date: fraudReport.generated_at })
    : undefined;

  return (
    <BorderContainer>
      <Grid
        container
        spacing={0}
        direction="column"
        data-provides="fraud-report-section"
      >
        <CollapsibleTitle
          isOpen={showReport}
          toggleIsOpen={() => setShowReport(!showReport)}
          title={t('panel.fraud_report.title')}
          subtitle={subtitle}
          unread={!showReport && isFraudReportUnread}
        />
        <Collapsible id="fraud-report-collapse" isOpen={!!showReport}>
          {isPending || !application?.paths?.fraudReportPath ? (
            <Grid
              container
              data-provides="skeleton-loader"
              data-provides-for="fraud-report"
              flex={1}
              sx={{ padding: 3 }}
            >
              <FraudLoading />
            </Grid>
          ) : (
            <FraudReport
              canRunFraudReports={canRunFraudReports}
              fraudReport={fraudReport || null}
              isPending={isPending}
              openFullReportModal={openFraudModal}
              onRunFraudReport={requestFraudReport}
              onRetryFailedFraudReport={retryFailedFraudReport}
              onRestoreFraudReport={restoreFraudReport}
              onResolveFraudReport={onResolveFraudReport}
              canReject={canReject}
              onRejectAsSecurityConcern={openRejectModal}
              canRejectAsSecurityConcern={canUseSecurityConcerns}
            />
          )}
        </Collapsible>
      </Grid>
    </BorderContainer>
  );
};

export default FraudReportCollapsible;
